/**
 * OCRTemplateSettings - Admin page for managing insurance card templates
 * and document field mappings for OCR extraction.
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect, useCallback } from 'react';
import {
  ScanLine,
  Plus,
  Edit2,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  Loader2,
  Globe,
  Building2,
  BarChart3,
  Trash2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  documentProcessingApi,
  CardTemplate,
  FieldMapping,
  DocumentFieldMappingConfig,
  CptMappingRule,
} from '@/services/api/documentProcessing';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface OCRTemplateSettingsProps {
  organizationId: number;
  organizationName: string;
}

const EXTRACTION_FIELDS = [
  'member_cin',
  'member_plan_id',
  'extracted_first_name',
  'extracted_last_name',
  'extracted_dob',
  'coverage_effective_date',
  'language',
  'plan_name',
];

const FIELD_TYPES = ['text', 'date', 'number', 'code'];
const TRANSFORMATIONS = ['', 'uppercase', 'trim', 'date_parse'];

interface TemplateFormState {
  card_type: string;
  plan_name: string;
  plan_variants: string;
  expected_fields: string;
  regex_patterns: Array<{ field: string; pattern: string }>;
  is_active: boolean;
}

const emptyTemplateForm: TemplateFormState = {
  card_type: '',
  plan_name: '',
  plan_variants: '',
  expected_fields: '',
  regex_patterns: [{ field: '', pattern: '' }],
  is_active: true,
};

function templateToForm(t: CardTemplate): TemplateFormState {
  const patterns = Object.entries(t.regex_patterns || {}).map(([field, pattern]) => ({
    field,
    pattern,
  }));
  if (patterns.length === 0) patterns.push({ field: '', pattern: '' });
  return {
    card_type: t.card_type,
    plan_name: t.plan_name,
    plan_variants: (t.plan_variants || []).join(', '),
    expected_fields: (t.expected_fields || []).join(', '),
    regex_patterns: patterns,
    is_active: t.is_active,
  };
}

function formToPayload(form: TemplateFormState) {
  const regex_patterns: Record<string, string> = {};
  for (const { field, pattern } of form.regex_patterns) {
    if (field && pattern) regex_patterns[field] = pattern;
  }
  return {
    card_type: form.card_type.trim().toLowerCase().replace(/\s+/g, '_'),
    plan_name: form.plan_name.trim(),
    plan_variants: form.plan_variants
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    expected_fields: form.expected_fields
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    regex_patterns,
    is_active: form.is_active,
  };
}

export function OCRTemplateSettings({ organizationId, organizationName }: OCRTemplateSettingsProps) {
  const tabbed = useTabbedChrome();
  const { fmt } = useFormattedDate();
  // -- Card Templates state --
  const [templates, setTemplates] = useState<CardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create / Edit
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<TemplateFormState>({ ...emptyTemplateForm });
  const [saving, setSaving] = useState(false);

  // Expanded row
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // -- Field Mappings state --
  const [fieldMappings, setFieldMappings] = useState<DocumentFieldMappingConfig | null>(null);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsError, setMappingsError] = useState<string | null>(null);
  const [mappingsSuccess, setMappingsSuccess] = useState<string | null>(null);
  const [mappingsSaving, setMappingsSaving] = useState(false);

  // ===== Fetch Card Templates =====
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await documentProcessingApi.listCardTemplates();
      setTemplates(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load card templates');
    } finally {
      setLoading(false);
    }
  }, []);

  // ===== Fetch Field Mappings =====
  const fetchFieldMappings = useCallback(async () => {
    setMappingsLoading(true);
    setMappingsError(null);
    try {
      const data = await documentProcessingApi.getFieldMappings();
      setFieldMappings(data);
    } catch (err: any) {
      setMappingsError(err.response?.data?.detail || 'Failed to load field mappings');
    } finally {
      setMappingsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchFieldMappings();
  }, [fetchTemplates, fetchFieldMappings]);

  // Auto-clear success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (mappingsSuccess) {
      const timer = setTimeout(() => setMappingsSuccess(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [mappingsSuccess]);

  // ===== Template CRUD =====
  const handleCreateTemplate = () => {
    setEditingId(null);
    setForm({ ...emptyTemplateForm });
    setShowForm(true);
  };

  const handleEditTemplate = (t: CardTemplate) => {
    setEditingId(t.id);
    setForm(templateToForm(t));
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyTemplateForm });
  };

  const handleSaveTemplate = async () => {
    if (!form.card_type.trim() || !form.plan_name.trim()) {
      setError('Card type and plan name are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = formToPayload(form);
      if (editingId) {
        await documentProcessingApi.updateCardTemplate(editingId, payload);
        setSuccess('Template updated successfully');
      } else {
        await documentProcessingApi.createCardTemplate(payload);
        setSuccess('Template created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyTemplateForm });
      await fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (t: CardTemplate) => {
    try {
      await documentProcessingApi.updateCardTemplate(t.id, { is_active: !t.is_active });
      setSuccess(`Template ${t.is_active ? 'deactivated' : 'activated'}`);
      await fetchTemplates();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to toggle template');
    }
  };

  // ===== Regex Pattern Rows =====
  const addRegexRow = () => {
    setForm((prev) => ({
      ...prev,
      regex_patterns: [...prev.regex_patterns, { field: '', pattern: '' }],
    }));
  };

  const removeRegexRow = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      regex_patterns: prev.regex_patterns.filter((_, i) => i !== idx),
    }));
  };

  const updateRegexRow = (idx: number, key: 'field' | 'pattern', value: string) => {
    setForm((prev) => ({
      ...prev,
      regex_patterns: prev.regex_patterns.map((row, i) =>
        i === idx ? { ...row, [key]: value } : row
      ),
    }));
  };

  // ===== Field Mappings CRUD =====
  const handleAddFieldMapping = () => {
    if (!fieldMappings) return;
    setFieldMappings({
      ...fieldMappings,
      field_mappings: [
        ...fieldMappings.field_mappings,
        {
          form_field: '',
          claim_field: '',
          field_type: 'text',
          is_required: false,
          ocr_field_hint: '',
        },
      ],
    });
  };

  const handleRemoveFieldMapping = (idx: number) => {
    if (!fieldMappings) return;
    setFieldMappings({
      ...fieldMappings,
      field_mappings: fieldMappings.field_mappings.filter((_, i) => i !== idx),
    });
  };

  const handleUpdateFieldMapping = (idx: number, key: keyof FieldMapping, value: any) => {
    if (!fieldMappings) return;
    setFieldMappings({
      ...fieldMappings,
      field_mappings: fieldMappings.field_mappings.map((m, i) =>
        i === idx ? { ...m, [key]: value } : m
      ),
    });
  };

  const handleAddCptRule = () => {
    if (!fieldMappings) return;
    setFieldMappings({
      ...fieldMappings,
      cpt_mapping_rules: [
        ...fieldMappings.cpt_mapping_rules,
        { visit_type_keyword: '', cpt_code: '', modifiers: [], default_units: 1 },
      ],
    });
  };

  const handleRemoveCptRule = (idx: number) => {
    if (!fieldMappings) return;
    setFieldMappings({
      ...fieldMappings,
      cpt_mapping_rules: fieldMappings.cpt_mapping_rules.filter((_, i) => i !== idx),
    });
  };

  const handleUpdateCptRule = (idx: number, key: keyof CptMappingRule, value: any) => {
    if (!fieldMappings) return;
    setFieldMappings({
      ...fieldMappings,
      cpt_mapping_rules: fieldMappings.cpt_mapping_rules.map((r, i) =>
        i === idx ? { ...r, [key]: value } : r
      ),
    });
  };

  const handleSaveFieldMappings = async () => {
    if (!fieldMappings) return;
    setMappingsSaving(true);
    setMappingsError(null);
    try {
      await documentProcessingApi.updateFieldMappings(fieldMappings);
      setMappingsSuccess('Field mappings saved successfully');
      await fetchFieldMappings();
    } catch (err: any) {
      setMappingsError(err.response?.data?.detail || 'Failed to save field mappings');
    } finally {
      setMappingsSaving(false);
    }
  };

  // ===== Computed =====
  const successRate = (t: CardTemplate) => {
    if (!t.times_scanned || t.times_scanned === 0) return null;
    return Math.round((t.successful_extractions / t.times_scanned) * 100);
  };

  // ===== Render =====
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {!tabbed && <ScanLine className="w-7 h-7 text-blue-600" />}
        <div>
          {!tabbed && <h1 className="text-2xl font-bold">OCR & Card Templates</h1>}
          <p className="text-sm text-gray-500">
            Manage insurance card templates and document field mappings for {organizationName}
          </p>
        </div>
      </div>

      <Tabs defaultValue="card-templates">
        <TabsList className="mb-4">
          <TabsTrigger value="card-templates">Insurance Card Templates</TabsTrigger>
          <TabsTrigger value="field-mappings">Document Field Mappings</TabsTrigger>
        </TabsList>

        {/* =========== TAB 1: Card Templates =========== */}
        <TabsContent value="card-templates">
          {/* Status messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">{success}</span>
            </div>
          )}

          {/* Add button */}
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-600">
              Templates define how OCR extracts fields from specific insurance card types.
              Global templates apply to all organizations. Create org-specific templates for custom cards.
            </p>
            <button
              onClick={handleCreateTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
            >
              <Plus className="w-4 h-4" /> Add Template
            </button>
          </div>

          {/* Create/Edit Form */}
          {showForm && (
            <Card className="mb-4 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg">
                  {editingId ? 'Edit Template' : 'New Card Template'}
                </CardTitle>
                <CardDescription>
                  Define extraction patterns for a specific insurance card type
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Row 1: Card Type + Plan Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Card Type <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.card_type}
                      onChange={(e) => setForm({ ...form, card_type: e.target.value })}
                      placeholder="e.g., sfhp, medi_cal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!!editingId}
                    />
                    <p className="text-xs text-gray-400 mt-1">Lowercase identifier, no spaces</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plan Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.plan_name}
                      onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                      placeholder="e.g., San Francisco Health Plan"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Row 2: Plan Variants + Expected Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plan Variants (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={form.plan_variants}
                      onChange={(e) => setForm({ ...form, plan_variants: e.target.value })}
                      placeholder="SFHP, SF Health Plan"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Alternative names used for auto-detecting this card type from OCR text
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expected Fields (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={form.expected_fields}
                      onChange={(e) => setForm({ ...form, expected_fields: e.target.value })}
                      placeholder="CIN, Member ID, DOB, Effective Date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Field labels expected on this card type
                    </p>
                  </div>
                </div>

                {/* Regex Patterns */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Regex Extraction Patterns
                  </label>
                  <p className="text-xs text-gray-400 mb-2">
                    Define regex patterns to extract specific fields from raw OCR text.
                    Each pattern should have one capture group for the field value.
                  </p>
                  <div className="space-y-2">
                    {form.regex_patterns.map((row, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <select
                          value={row.field}
                          onChange={(e) => updateRegexRow(idx, 'field', e.target.value)}
                          className="w-48 px-2 py-2 border border-gray-300 rounded-md text-sm bg-white"
                        >
                          <option value="">Select field...</option>
                          {EXTRACTION_FIELDS.map((f) => (
                            <option key={f} value={f}>
                              {f}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          value={row.pattern}
                          onChange={(e) => updateRegexRow(idx, 'pattern', e.target.value)}
                          placeholder="e.g., CIN[:\s]+(\w+)"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => removeRegexRow(idx)}
                          className="p-2 text-gray-400 hover:text-red-600"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addRegexRow}
                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Plus className="w-3 h-3" /> Add Pattern
                    </button>
                  </div>
                </div>

                {/* Active toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="template-active"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="template-active" className="text-sm text-gray-700">
                    Active
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={handleSaveTemplate}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {editingId ? 'Update Template' : 'Create Template'}
                  </button>
                  <button
                    onClick={handleCancelForm}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Templates List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-500">Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <ScanLine className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No card templates found. Create one to get started.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {templates.map((t) => (
                <Card
                  key={t.id}
                  className={`overflow-hidden ${!t.is_active ? 'opacity-60' : ''}`}
                >
                  {/* Summary row */}
                  <div
                    className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                  >
                    {/* Scope badge */}
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.is_global
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {t.is_global ? (
                        <><Globe className="w-3 h-3" /> Global</>
                      ) : (
                        <><Building2 className="w-3 h-3" /> Org</>
                      )}
                    </span>

                    {/* Plan info */}
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm">{t.plan_name}</span>
                      <span className="ml-2 text-xs text-gray-400 font-mono">{t.card_type}</span>
                    </div>

                    {/* Variants count */}
                    {t.plan_variants && t.plan_variants.length > 0 && (
                      <span className="text-xs text-gray-500">
                        {t.plan_variants.length} variant{t.plan_variants.length !== 1 ? 's' : ''}
                      </span>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1" title="Times scanned">
                        <BarChart3 className="w-3 h-3" />
                        {t.times_scanned}
                      </span>
                      {successRate(t) !== null && (
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            successRate(t)! >= 70
                              ? 'bg-green-100 text-green-700'
                              : successRate(t)! >= 40
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {successRate(t)}%
                        </span>
                      )}
                    </div>

                    {/* Status */}
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        t.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {t.is_active ? 'Active' : 'Inactive'}
                    </span>

                    {/* Expand chevron */}
                    {expandedId === t.id ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>

                  {/* Expanded detail */}
                  {expandedId === t.id && (
                    <div className="border-t px-4 py-3 bg-gray-50 space-y-3 text-sm">
                      {/* Plan Variants */}
                      {t.plan_variants && t.plan_variants.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-600">Plan Variants: </span>
                          {t.plan_variants.map((v, i) => (
                            <span
                              key={i}
                              className="inline-block mr-1 mb-1 px-2 py-0.5 bg-white border rounded text-xs"
                            >
                              {v}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Expected Fields */}
                      {t.expected_fields && t.expected_fields.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-600">Expected Fields: </span>
                          {t.expected_fields.map((f, i) => (
                            <span
                              key={i}
                              className="inline-block mr-1 mb-1 px-2 py-0.5 bg-white border rounded text-xs"
                            >
                              {f}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Regex Patterns */}
                      {t.regex_patterns && Object.keys(t.regex_patterns).length > 0 && (
                        <div>
                          <span className="font-medium text-gray-600">Regex Patterns:</span>
                          <div className="mt-1 bg-white border rounded-md overflow-hidden">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-3 py-1.5 text-left font-medium text-gray-600">Field</th>
                                  <th className="px-3 py-1.5 text-left font-medium text-gray-600">Pattern</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(t.regex_patterns).map(([field, pattern]) => (
                                  <tr key={field} className="border-t">
                                    <td className="px-3 py-1.5 font-mono text-blue-700">{field}</td>
                                    <td className="px-3 py-1.5 font-mono text-gray-600">{pattern}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Stats detail */}
                      <div className="flex gap-6 text-xs text-gray-500">
                        <span>Scanned: {t.times_scanned}</span>
                        <span>Successful: {t.successful_extractions}</span>
                        <span>Avg Confidence: {(t.avg_confidence * 100).toFixed(1)}%</span>
                        <span>Updated: {fmt.short(t.updated_at)}</span>
                      </div>

                      {/* Actions */}
                      {!t.is_global && (
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => handleEditTemplate(t)}
                            className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-xs hover:bg-white"
                          >
                            <Edit2 className="w-3 h-3" /> Edit
                          </button>
                          <button
                            onClick={() => handleToggleActive(t)}
                            className={`flex items-center gap-1 px-3 py-1.5 border rounded text-xs ${
                              t.is_active
                                ? 'border-orange-300 text-orange-700 hover:bg-orange-50'
                                : 'border-green-300 text-green-700 hover:bg-green-50'
                            }`}
                          >
                            {t.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      )}
                      {t.is_global && (
                        <p className="text-xs text-gray-400 italic">
                          Global templates are read-only. Create an org-specific template to override.
                        </p>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* =========== TAB 2: Field Mappings =========== */}
        <TabsContent value="field-mappings">
          {mappingsError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{mappingsError}</span>
              <button onClick={() => setMappingsError(null)} className="ml-auto text-red-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {mappingsSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">{mappingsSuccess}</span>
            </div>
          )}

          {mappingsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-500">Loading field mappings...</span>
            </div>
          ) : !fieldMappings ? (
            <Card className="p-8 text-center text-gray-500">
              <p>No field mapping configuration found.</p>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Document type info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">OCR Field Mappings</CardTitle>
                  <CardDescription>
                    Configure how OCR-extracted labels map to claim fields.
                    Document type: <span className="font-mono font-medium">{fieldMappings.document_type}</span>
                    {fieldMappings.form_name && <> | Form: {fieldMappings.form_name}</>}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Defaults */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Place of Service
                      </label>
                      <input
                        type="text"
                        value={fieldMappings.default_place_of_service || ''}
                        onChange={(e) =>
                          setFieldMappings({ ...fieldMappings, default_place_of_service: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Diagnosis Code
                      </label>
                      <input
                        type="text"
                        value={fieldMappings.default_diagnosis_code || ''}
                        onChange={(e) =>
                          setFieldMappings({ ...fieldMappings, default_diagnosis_code: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Form Name</label>
                      <input
                        type="text"
                        value={fieldMappings.form_name || ''}
                        onChange={(e) =>
                          setFieldMappings({ ...fieldMappings, form_name: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>

                  {/* Field Mappings Table */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium text-sm text-gray-700">Field Mappings</h3>
                      <button
                        onClick={handleAddFieldMapping}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Plus className="w-3 h-3" /> Add Field
                      </button>
                    </div>
                    {fieldMappings.field_mappings.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">
                        No field mappings configured. Add fields to define how OCR labels map to claim fields.
                      </p>
                    ) : (
                      <div className="border rounded-md overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Form Field</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Claim Field</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Required</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">OCR Hint</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Transform</th>
                              <th className="px-3 py-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {fieldMappings.field_mappings.map((m, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={m.form_field}
                                    onChange={(e) =>
                                      handleUpdateFieldMapping(idx, 'form_field', e.target.value)
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                                    placeholder="OCR label"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={m.claim_field}
                                    onChange={(e) =>
                                      handleUpdateFieldMapping(idx, 'claim_field', e.target.value)
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-mono"
                                    placeholder="db_field"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <select
                                    value={m.field_type}
                                    onChange={(e) =>
                                      handleUpdateFieldMapping(idx, 'field_type', e.target.value)
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs bg-white"
                                  >
                                    {FIELD_TYPES.map((ft) => (
                                      <option key={ft} value={ft}>
                                        {ft}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-2 py-1 text-center">
                                  <input
                                    type="checkbox"
                                    checked={m.is_required}
                                    onChange={(e) =>
                                      handleUpdateFieldMapping(idx, 'is_required', e.target.checked)
                                    }
                                    className="w-4 h-4"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={m.ocr_field_hint || ''}
                                    onChange={(e) =>
                                      handleUpdateFieldMapping(idx, 'ocr_field_hint', e.target.value)
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-mono"
                                    placeholder="alias1|alias2"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <select
                                    value={m.transformation || ''}
                                    onChange={(e) =>
                                      handleUpdateFieldMapping(idx, 'transformation', e.target.value)
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs bg-white"
                                  >
                                    {TRANSFORMATIONS.map((tr) => (
                                      <option key={tr} value={tr}>
                                        {tr || '(none)'}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-2 py-1">
                                  <button
                                    onClick={() => handleRemoveFieldMapping(idx)}
                                    className="p-1 text-gray-400 hover:text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* CPT Mapping Rules */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium text-sm text-gray-700">CPT Mapping Rules</h3>
                      <button
                        onClick={handleAddCptRule}
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Plus className="w-3 h-3" /> Add Rule
                      </button>
                    </div>
                    {fieldMappings.cpt_mapping_rules.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">
                        No CPT mapping rules configured.
                      </p>
                    ) : (
                      <div className="border rounded-md overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">
                                Visit Type Keyword
                              </th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">CPT Code</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Modifiers</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Units</th>
                              <th className="px-3 py-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {fieldMappings.cpt_mapping_rules.map((rule, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={rule.visit_type_keyword}
                                    onChange={(e) =>
                                      handleUpdateCptRule(idx, 'visit_type_keyword', e.target.value)
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                                    placeholder="prenatal"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={rule.cpt_code}
                                    onChange={(e) =>
                                      handleUpdateCptRule(idx, 'cpt_code', e.target.value)
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-mono"
                                    placeholder="99214"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    value={(rule.modifiers || []).join(', ')}
                                    onChange={(e) =>
                                      handleUpdateCptRule(
                                        idx,
                                        'modifiers',
                                        e.target.value
                                          .split(',')
                                          .map((s) => s.trim())
                                          .filter(Boolean)
                                      )
                                    }
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                                    placeholder="HD, 25"
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="number"
                                    value={rule.default_units}
                                    onChange={(e) =>
                                      handleUpdateCptRule(idx, 'default_units', parseInt(e.target.value) || 1)
                                    }
                                    className="w-20 px-2 py-1 border border-gray-200 rounded text-xs"
                                    min={1}
                                  />
                                </td>
                                <td className="px-2 py-1">
                                  <button
                                    onClick={() => handleRemoveCptRule(idx)}
                                    className="p-1 text-gray-400 hover:text-red-600"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Save */}
                  <button
                    onClick={handleSaveFieldMappings}
                    disabled={mappingsSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                  >
                    {mappingsSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    Save Field Mappings
                  </button>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
